/* eslint-disable react-hooks/exhaustive-deps */
import BarChartIcon from '@mui/icons-material/BarChart';
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MenuIcon from '@mui/icons-material/Menu';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Stack } from '@mui/material';
import { saveAs } from 'file-saver';
import i18next from 'i18next';
import { lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { CURRENCY, WEBSITE_URL } from '../../../../config/appConfig';
import { AppRoutes } from '../../../../constants/appRoutes';
import { BecomeProStatuses } from '../../../../constants/becomeProStatus';
import { EarningsTerm } from '../../../../constants/earningTerms';
import { InvestorType } from '../../../../constants/investorType';
import { InvestorChartTypes } from '../../../../constants/investorChartTypes';
import { CsvHelper } from '../../../../helpers/csvHelper';
import { currencyFormatterHelper } from '../../../../helpers/currencyFormatterHelper';
import { ar as arLocale } from 'date-fns/locale';
import { DateHelper } from '../../../../helpers/dateHelper';
import { chartData } from '../../../../helpers/investorChartOptions';
import { CapitalBarGraphData } from '../../../../helpers/investorRepaymentData';
import { buildLocalizeRoute } from '../../../../helpers/languageHelper';
import { getLoginInfo, getRegistrationData, getRegistrationInfo, getUser } from '../../../../services/authService';
import { BecomeProService } from '../../../../services/becomeProService';
import { BucketService } from '../../../../services/bucketService';
import { LanguageService } from '../../../../services/languageService';
import { CashbackBanner } from '../../../../shared/components/cashbackBanner';
import { Section } from '../../../../shared/components/common/section';
import { SummaryCard } from '../../../../shared/components/common/summaryCard';
import { CustomTooltip } from '../../../../shared/components/common/tooltip';
import { ContactUsDialog } from '../../../../shared/components/contactUsDialog';
import { ButtonWrapper } from '../../../../shared/components/formUtilities/useButton';
import { TextWrapper } from '../../../../shared/components/formUtilities/useTextWrapper';
import { HowItWorksDialog } from '../../../../shared/components/howItWorkDialog';
import { SummaryLoader } from '../../../../shared/components/investors/loaders/summaryLoader';
import { RepaymentChartLoader } from '../../../../shared/components/investors/loaders/summaryLoader/repaymentChartLoader';
import { InvestorService } from '../../investorService';
import { LatestOpportunities } from '../../latestOpportunities';
import { PreventDepositDialog } from '../transfers/deposit/preventDepositDialog';

const ReactApexChart = lazy(() => import('react-apexcharts'));

const QuickActionCard = ({ icon, label, green, onClick, to, href }) => {
	const bgClass = green ? 'bg-investor-green' : 'bg-investor-bg';
	const iconBgClass = green ? 'bg-investor-bg' : 'bg-investor-green';
	const iconColorClass = green ? 'text-investor-green' : 'text-investor-deep';
	const labelClass = green ? 'text-investor-bg' : 'text-investor-green';
	const content = (
		<Box className={`${bgClass} rounded-xl p-4 h-full flex flex-col items-start min-h-28 cursor-pointer gap-4`}>
			<Box className={`${iconBgClass} ${iconColorClass} rounded-full p-2 self-end`}>{icon}</Box>
			<span className={`font-sans text-xl font-normal text-left max-w-[70%] capitalize mt-auto ${labelClass}`}>{label}</span>
		</Box>
	);
	if (href) return <a href={href} target="_blank" rel="noreferrer" className="no-underline">{content}</a>;
	if (to) return <Link to={to} className="no-underline">{content}</Link>;
	return <Box onClick={onClick}>{content}</Box>;
};

export const InvestorSummary = () => {
	const becomeProService = new BecomeProService();
	const investorService = new InvestorService();
	const bucketService = new BucketService();
	const languageService = new LanguageService();
	const navigate = useNavigate();

	const [netEarnings, setNetEarnings] = useState(null);
	const [activeInvestmentCount, setActiveInvestmentCount] = useState(0);
	const [activeInvestments, setActiveInvestments] = useState([]);
	const [isShowCSVMenu, setShowCSVMenu] = useState(false);
	const [loading, setLoading] = useState(true);
	const [loadingRepayment, setLoadingRepayment] = useState(true);
	const [showTransferPopup, setShowTransferPopup] = useState(false);
	const [topOffset, setTopOffset] = useState(0);
	const [repaymentChartOptions, setRepaymentChartOptions] = useState(
		chartData(InvestorChartTypes.RepaymentChart, languageService.isArabic())
	);
	const [proStatus, setProStatus] = useState(null);
	const [summary, setSummary] = useState(null);
	const [showHowWorks, setShowHowWorks] = useState(false);
	const [showContacUs, setContactUs] = useState(false);
	const { t } = useTranslation();

	const hasActiveInvestment = () => {
		return activeInvestments?.length && activeInvestmentCount ? true : false;
	};

	const isAr = () => {
		return languageService.isArabic();
	};

	useEffect(() => {
		getInvestorsSummary();
		loadData();
		setRepaymentChartOptions(chartData(InvestorChartTypes.RepaymentChart));
		getBecomeProStatus();
		loadNetEarnings();
	}, []);


	const loadData = async () => {
		setLoading(true);
		try {
			const [activeInvestmentCount] = await Promise.all([
				bucketService.activateInvestmentsCount(),
			]).catch((err) => {
				setLoading(false);
			});
			setLoading(false);
			getActiveInvestments(activeInvestmentCount.data);
		} catch { }
	};

	const getActiveInvestments = (activeInvestmentCount) => {
		setLoadingRepayment(true);
		investorService
			.activateInvestments()
			.then((activeInvestments) => {
				setActiveInvestmentCount(activeInvestmentCount);
				setActiveInvestments(activeInvestments.data);
				setLoadingRepayment(false);
				setTimeout(() => {
					if (activeInvestments.data?.length && activeInvestmentCount) {
						setValueCapitalChart(activeInvestments.data);
					} else {
						setDefaultValueToBarChart();
					}
				});
			})
			.catch((err) => {
				setLoadingRepayment(false);
			});
	};

	const getBecomeProStatus = () => {
		becomeProService.getBecomeProStatus()
			.then((resp) => { setProStatus(resp.data); })
			.catch(() => { });
	};

	const isShowBecomeProBtn = () => proStatus?.status !== BecomeProStatuses.Approved;

	const isProfessional = () => {
		const investorType = getLoginInfo()?.investorType
			|| getRegistrationInfo()?.investorType
			|| getRegistrationData()?.investorType;
		return investorType === InvestorType.Professional;
	};

	const loadNetEarnings = () => {
		investorService
			.getNetEarnings(EarningsTerm.AllTime)
			.then((resp) => {
				setNetEarnings(resp.data);
			})
			.catch((err) => { });
	};

	const setValueCapitalChart = (activeInvestments = []) => {
		try {
			repaymentChartOptions.options.plotOptions.bar.columnWidth = '60%';
			repaymentChartOptions.options.colors = ['#063236', '#00D175'];
			repaymentChartOptions.options.dataLabels = { enabled: false };
			repaymentChartOptions.series = [
				{
					name: t('principal'),
					data: activeInvestments.map((data) => Math.round(data.principal)),
				},
				{
					name: t('profit'),
					data: activeInvestments.map((data) => Math.round(data.profit)),
				},
			];
			const locale = isAr() ? arLocale : null;
			const xCategories = activeInvestments.map((data) => [
				DateHelper.formatDate(data.date, 'MMM', locale),
				DateHelper.formatDate(data.date, 'yyyy'),
			]);
			repaymentChartOptions.options.xaxis = {
				categories: xCategories,
			};
			repaymentChartOptions.options.responsive[0].options.xaxis.categories = xCategories;
			setRepaymentChartOptions({ ...repaymentChartOptions });
		} catch { }
	};

	const onDownloadCSV = () => {
		let series = [];
		if (activeInvestments?.length && activeInvestmentCount) {
			activeInvestments.forEach((data) => {
				series.push({
					month: DateHelper.formatDate(data?.date, 'MMM'),
					profit: data?.profit,
					principal: data?.principal,
					total: data?.profit + data?.principal,
				});
			});
		} else {
			repaymentChartOptions.series[0].data.forEach((resp) => {
				series.push({
					month: resp.x,
					repayments: resp.y,
				});
			});
		}

		const date = DateHelper.formatDate(new Date());
		const fileName = `Repayment-Schedule-${date}.csv`;
		const csv = CsvHelper.serializeArrayToCsv(series);
		const blob = new Blob([csv], {
			type: 'application/json',
		});
		saveAs(blob, fileName);
	};

	// const customDownloadBtn = () => {
	// 	return (
	// 		<Box
	// 			className={`cursor-pointer relative`}>
	// 			<ButtonWrapper
	// 				classList="!bg-white"
	// 				onClick={() => {
	// 					setShowCSVMenu(!isShowCSVMenu);
	// 				}}
	// 				icon={<MenuIcon className={`!text-xl !text-investor-slate`} />}
	// 				iconButton></ButtonWrapper>
	// 			{isShowCSVMenu ? (
	// 				<Box
	// 					className={`absolute border border-solid text-center p-1 rounded min-w-28 right-1.5 z-[999] bg-white ${isAr() ? '!right-auto left-1.5' : ''
	// 						}`}>
	// 					<p
	// 						onClick={() => {
	// 							onDownloadCSV();
	// 							setShowCSVMenu(!isShowCSVMenu);
	// 						}}
	// 						className="p-1 hover:bg-neutral-200 !text-xs ">
	// 						{t('downloadCSV')}
	// 					</p>
	// 				</Box>
	// 			) : null}
	// 		</Box>
	// 	);
	// };

	const setDefaultValueToBarChart = () => {
		repaymentChartOptions.options.dataLabels = { enabled: false };
		repaymentChartOptions.options.colors = ['#00D175'];
		repaymentChartOptions.options.responsive[0].options.dataLabels = { enabled: false };
		const arabicMonths = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
		repaymentChartOptions.series[0].data =
			CapitalBarGraphData.sortByCurrentDate(isAr() ? arabicMonths : undefined);
		repaymentChartOptions.series[0].name = t('repayments');
		setRepaymentChartOptions({ ...repaymentChartOptions });
	};

	const getInvestorsSummary = () => {
		investorService
			.getInvestorsSummary()
			.then((resp) => {
				setSummary(resp.data);
			})
			.catch((err) => { });
	};


	const onCashackLayoutChange = () => {
		if (loading) return;

		const element = document.getElementById("mp-announcement-bar");
		if (!element) return;

		const updateHeight = () => {
			setTopOffset(element.getBoundingClientRect().height + 3);
		};

		// Initial height update
		updateHeight();

		// Observe changes in height dynamically
		const resizeObserver = new ResizeObserver(() => {
			requestAnimationFrame(updateHeight); // Ensures smoother updates
		});
		resizeObserver.observe(element);
	};

	return (
		<Box>
			{!loading ? (
				<Stack gap={3}>
					<CashbackBanner onLayoutChange={() => onCashackLayoutChange()} />
					<Box className={`grid grid-cols-1 md:grid-cols-3 gap-6`} sx={{ marginTop: `${topOffset}px` }}>
						<SummaryCard
							classList="p-4"
							children={
								<Box className="flex flex-col h-full justify-between space-y-4">
									<Box>
										<Box className="flex items-center gap-1 mb-1">
											<TextWrapper classList="!font-bold !text-sm !text-investor-deep" text={t('totalAssets')} />
											<CustomTooltip
												iconSizeClass="!text-sm"
												iconClassList="!text-primary !top-0"
												id="totalAssets"
												text="totalAssetsSummaryInfo" />
										</Box>
										<Box className="!font-bold !text-3xl !text-investor-deep currency-symbol mb-2">
											{`${CURRENCY} ${currencyFormatterHelper(summary?.portfolioTotal, 0)}`}
										</Box>
										<Box className="flex w-full my-3 h-12">
											{summary?.availableCash > 0 && (
												<Box className="h-full flex-shrink-0 rounded-l-xl" style={{ width: `${summary.availableCash / summary.portfolioTotal * 100}%`, backgroundColor: '#0d2b22' }} />
											)}
											{summary?.ordersAmount > 0 && (
												<Box className="h-full flex-shrink-0" style={{ width: `${summary.ordersAmount / summary.portfolioTotal * 100}%`, backgroundColor: '#7de5c9' }} />
											)}
											{summary?.outstandingAmount > 0 && (
												<Box className="h-full flex-1 rounded-r-xl" style={{ backgroundColor: '#33cd87' }} />
											)}
										</Box>
										<Box className="flex items-center justify-between py-3 border-t border-investor-deep">
											<Box className="flex items-center gap-2">
												<Box className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#0d2b22' }} />
												<TextWrapper classList="!text-xs !text-investor-deep" text={t('availableFunds')} />
											</Box>
											<Box className="flex items-center gap-1">
												<Box className="!text-xs !font-bold !text-investor-deep currency-symbol">{`${CURRENCY} ${currencyFormatterHelper(summary?.availableCash, 2)}`}</Box>
												<CustomTooltip iconSizeClass="!text-xs" iconClassList="!top-0" id="availableFunds" text="availableFundsSummaryInfo" />
											</Box>
										</Box>
										<Box className="flex items-center justify-between py-3 border-t border-investor-deep">
											<Box className="flex items-center gap-2">
												<Box className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#7de5c9' }} />
												<TextWrapper classList="!text-xs !text-investor-deep" text={t('orders')} />
											</Box>
											<Box className="flex items-center gap-1">
												<Box className="!text-xs !font-bold !text-investor-deep currency-symbol">{`${CURRENCY} ${currencyFormatterHelper(summary?.ordersAmount, 2)}`}</Box>
												<CustomTooltip iconSizeClass="!text-xs" iconClassList="!top-0" id="orders" text="ordersSummaryInfo" />
											</Box>
										</Box>
										<Box className="flex items-center justify-between py-3 border-t border-investor-deep">
											<Box className="flex items-center gap-2">
												<Box className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#33cd87' }} />
												<TextWrapper classList="!text-xs !text-investor-deep" text={t('investments')} />
											</Box>
											<Box className="flex items-center gap-1">
												<Box className="!text-xs !font-bold !text-investor-deep currency-symbol">{`${CURRENCY} ${currencyFormatterHelper(summary?.outstandingAmount, 2)}`}</Box>
												<CustomTooltip iconSizeClass="!text-xs" iconClassList="!top-0" id="investments" text="investmentsSummaryInfo" />
											</Box>
										</Box>
									</Box>
									<Box className="flex justify-end">
										<ButtonWrapper
											onClick={() => {
												if (!getUser()?.isApproved) {
													setShowTransferPopup(true);
													return;
												}
												navigate(buildLocalizeRoute(`${AppRoutes.Investor}/${AppRoutes.ITransfer}/${AppRoutes.ITransferMethods}`));
											}}
											label={
												<Box className="flex items-center gap-2">
													<ArrowCircleDownOutlinedIcon className="!text-[32px]" />
													<span>{t('deposit').toUpperCase()}</span>
												</Box>
											}
											classList="!bg-investor-forest !text-white !rounded-full !px-6 !min-w-28 h-10 !shadow-none" />
									</Box>
								</Box>
							}
						/>
						<SummaryCard
							classList="p-4"
							children={
								<Box className="flex flex-col h-full justify-between space-y-4">
									<Box>
										<Box className="flex items-center gap-1 mb-1">
											<TextWrapper classList="!font-bold !text-sm !text-investor-deep" text={t('investments')} />
											<CustomTooltip
												iconSizeClass="!text-sm"
												iconClassList="!text-primary !top-0"
												id="investmentsCard"
												text="investmentsSummaryInfo" />
										</Box>
										<Box className="!font-bold !text-3xl !text-investor-deep currency-symbol mb-2">
											{`${CURRENCY} ${currencyFormatterHelper(summary?.outstandingAmount, 0)}`}
										</Box>
										<Box className="flex w-full my-3 h-12">
											{summary?.plannedOutstandingAmount > 0 && (
												<Box className="h-full flex-shrink-0 rounded-l-xl" style={{ width: `${summary.plannedOutstandingAmount / summary.outstandingAmount * 100}%`, backgroundColor: '#33cd87' }} />
											)}
											{summary?.pastDue30 > 0 && (
												<Box className="h-full flex-shrink-0" style={{ width: `${summary.pastDue30 / summary.outstandingAmount * 100}%`, backgroundColor: '#7de5c9' }} />
											)}
											{summary?.pastDue3090 > 0 && (
												<Box className="h-full flex-shrink-0" style={{ width: `${summary.pastDue3090 / summary.outstandingAmount * 100}%`, backgroundColor: '#0d2b22' }} />
											)}
											{summary?.pastDueMoreThan90 > 0 && (
												<Box className="h-full flex-1 rounded-r-xl" style={{ backgroundColor: '#f87171' }} />
											)}
										</Box>
										<Box className="flex items-center justify-between py-3 border-t border-investor-deep">
											<Box className="flex items-center gap-2">
												<Box className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#33cd87' }} />
												<TextWrapper classList="!text-xs !text-investor-deep" text={t('current')} />
											</Box>
											<Box className="!text-xs !font-bold !text-investor-deep currency-symbol">{`${CURRENCY} ${currencyFormatterHelper(summary?.plannedOutstandingAmount, 2)}`}</Box>
										</Box>
										<Box className="flex items-center justify-between py-3 border-t border-investor-deep">
											<Box className="flex items-center gap-2">
												<Box className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#7de5c9' }} />
												<TextWrapper classList="!text-xs !text-investor-deep" text={t('pastDue1to30')} />
											</Box>
											<Box className="!text-xs !font-bold !text-investor-deep currency-symbol">{`${CURRENCY} ${currencyFormatterHelper(summary?.pastDue30, 2)}`}</Box>
										</Box>
										<Box className="flex items-center justify-between py-3 border-t border-investor-deep">
											<Box className="flex items-center gap-2">
												<Box className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#0d2b22' }} />
												<TextWrapper classList="!text-xs !text-investor-deep" text={t('pastDue31to90')} />
											</Box>
											<Box className="!text-xs !font-bold !text-investor-deep currency-symbol">{`${CURRENCY} ${currencyFormatterHelper(summary?.pastDue3090, 2)}`}</Box>
										</Box>
										<Box className="flex items-center justify-between py-3 border-t border-investor-deep">
											<Box className="flex items-center gap-2">
												<Box className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#f87171' }} />
												<TextWrapper classList="!text-xs !text-investor-deep" text={t('pastDue90Plus')} />
											</Box>
											<Box className="!text-xs !font-bold !text-investor-deep currency-symbol">{`${CURRENCY} ${currencyFormatterHelper(summary?.pastDueMoreThan90, 2)}`}</Box>
										</Box>
									</Box>
									<Box className="flex justify-end">
										<ButtonWrapper
											onClick={() => {
												navigate(buildLocalizeRoute(`${AppRoutes.Investor}/${AppRoutes.Marketplace}`));
											}}
											label={
												<Box className="flex items-center gap-2">
													<ArrowCircleRightOutlinedIcon className="!text-[32px]" />
													<span>{t('marketplace').toUpperCase()}</span>
												</Box>
											}
											classList="!bg-investor-forest !text-white !rounded-full !px-6 !min-w-36 h-10 !shadow-none" />
									</Box>
								</Box>
							}></SummaryCard>
						<SummaryCard
							classList="p-4"
							children={
								<Box className="flex flex-col h-full justify-between space-y-4">
									<Box>
										<TextWrapper classList="!font-bold !text-sm !text-investor-deep" text={t('totalIncome')} />
										<Box className="!font-bold !text-3xl !text-investor-deep currency-symbol mb-2">
											{`${CURRENCY} ${currencyFormatterHelper(netEarnings?.netEarnings, 2)}`}
										</Box>
										<Box className="flex items-center gap-1 mb-1">
											<span className="text-xs text-investor-deep">
												<span className="font-bold text-sm">{t('netYield')}</span>
												{' '}
												<span className="font-normal">{t('netYieldExpectedSuffix')}</span>
											</span>
											<CustomTooltip
												iconSizeClass="!text-xs"
												iconClassList="!text-primary !top-0"
												id="expectedNetYield"
												text="expectedNetYieldSummaryInfo" />
										</Box>
										<Box className="!font-bold !text-2xl !text-investor-deep mb-3">{`${summary?.netYield}%`}</Box>
										<Box className="flex items-center justify-between py-3 border-t border-investor-deep">
											<TextWrapper classList="!text-xs !text-investor-deep" text={t('profitReceived')} />
											<Box className="flex items-center gap-1">
												<Box className="!text-xs !font-bold !text-investor-deep currency-symbol">{`${CURRENCY} ${currencyFormatterHelper(netEarnings?.realisedEarnings, 2)}`}</Box>
												<CustomTooltip iconSizeClass="!text-xs" iconClassList="!top-0" id="profitReceived" text="profitReceivedSummaryInfo" />
											</Box>
										</Box>
										<Box className="flex items-center justify-between py-3 border-t border-investor-deep">
											<TextWrapper classList="!text-xs !text-investor-deep" text={t('profitForecast')} />
											<Box className="flex items-center gap-1">
												<Box className="!text-xs !font-bold !text-investor-deep currency-symbol">{`${CURRENCY} ${currencyFormatterHelper(netEarnings?.unRealisedEarnings, 2)}`}</Box>
												<CustomTooltip iconSizeClass="!text-xs" iconClassList="!top-0" id="profitForecast" text="profitForecastSummaryInfo" />
											</Box>
										</Box>
										<Box className="flex items-center justify-between py-3 border-t border-investor-deep">
											<TextWrapper classList="!text-xs !text-investor-deep" text={t('losses')} />
											<Box className="flex items-center gap-1">
												<Box className="!text-xs !font-bold !text-investor-deep currency-symbol">{`${CURRENCY} ${currencyFormatterHelper(netEarnings?.losses, 2)}`}</Box>
												<CustomTooltip iconSizeClass="!text-xs" iconClassList="!top-0" id="losses" text="lossesSummaryInfo" />
											</Box>
										</Box>
									</Box>
									<Box className="flex justify-end">
										<ButtonWrapper
											onClick={() => {
												navigate(buildLocalizeRoute(`${AppRoutes.Investor}/${AppRoutes.MyInvestments}`));
											}}
											label={
												<Box className="flex items-center gap-2">
													<ArrowCircleDownOutlinedIcon className="!text-[32px]" />
													<span>{t('investments').toUpperCase()}</span>
												</Box>
											}
											classList="!bg-investor-forest !text-white !rounded-full !px-6 !min-w-36 h-10 !shadow-none" />
									</Box>
								</Box>
							}></SummaryCard>
					</Box>
					<Box className="grid grid-cols-1 md:grid-cols-3 gap-6">
						<Section
							classList="md:!col-span-2 p-6"
							isShadow={true}
							sx={{ backgroundColor: '#f3f5f5', boxShadow: 'none', borderRadius: '12px' }}
							children={
								!loadingRepayment ? (
									<>
										<Stack className="w-full">
											<Box className="flex items-center justify-between w-full mb-2">
												<Box className="flex items-center gap-1">
													<TextWrapper classList="!sub-heading" text={t('repaymentSchedule')} />
													<CustomTooltip
														id="repayment"
														text="repaymentScheduleExpectedInfo" />
												</Box>
												<Box className="flex items-center gap-3">
													<span className="px-5 py-1.5 text-xs font-bold rounded-full bg-investor-deep text-white">
														{t('principal').toUpperCase()}
													</span>
													<span className="px-5 py-1.5 text-xs font-bold rounded-full bg-investor-green text-white">
														{t('profit').toUpperCase()}
													</span>
													{/* {customDownloadBtn()} */}
												</Box>
											</Box>
											<style>{`
											#repaymentChart .apexcharts-gridlines-horizontal line:not(:last-child) {
												stroke-width: 0.5px;
											}
										`}</style>
											<Box className="overflow-x-auto w-full">
												<Box className="min-w-[600px]">
													<ReactApexChart
														height={'300px'}
														options={repaymentChartOptions.options}
														series={repaymentChartOptions.series}
														type="bar"
													/>
												</Box>
											</Box>
											{!hasActiveInvestment() ? (
												<TextWrapper
													type="caption"
													text={t('repaymentScheduleNoData')}></TextWrapper>
											) : null}
										</Stack>
									</>
								) : (
									<RepaymentChartLoader />
								)
							}
						/>
						<LatestOpportunities />
					</Box>
					<Box className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
						<QuickActionCard
							icon={<SettingsIcon />}
							label={t('howItWorks')}
							onClick={() => setShowHowWorks(true)} />
						<QuickActionCard
							icon={<TrendingUpIcon />}
							label={isShowBecomeProBtn() && !isProfessional() ? t('upgradeYourAccount') : t('youArePro')}
							to={buildLocalizeRoute(`${AppRoutes.Investor}/${AppRoutes.Profile}/${AppRoutes.BecomeAPro}`)}
						/>
						<QuickActionCard
							icon={<ReceiptLongIcon />}
							label={t('transactionStatements')}
							to={buildLocalizeRoute(`${AppRoutes.Investor}/${AppRoutes.ITransactionStatements}`)} />
						<QuickActionCard
							icon={<BarChartIcon />}
							label={t('creditScoringProcess')}
							href={`${WEBSITE_URL}${i18next.language}/credit-assessment`} />
						<QuickActionCard
							icon={<MailOutlineIcon />}
							label={t('getInTouch')}
							green
							onClick={() => setContactUs(true)} />
						<QuickActionCard
							icon={<HelpOutlineIcon />}
							label={t('frequentlyAskedQuestions')}
							green
							href={`${WEBSITE_URL}${i18next.language}/faq`} />
					</Box>
				</Stack>
			) : (
				<SummaryLoader></SummaryLoader>
			)}
			{showHowWorks ? <HowItWorksDialog showHowWorks={showHowWorks} onClose={(e) => {
				setShowHowWorks(e);
			}} /> : null}

			{showContacUs ? <ContactUsDialog state={showContacUs} onClose={(e) => {
				setContactUs(false);
			}} /> : null}

			{showTransferPopup ?
				<PreventDepositDialog
					onClose={() => {
						setShowTransferPopup(false);
					}} state={showTransferPopup} /> : null}
		</Box>
	);
};
